import { api, ApiError } from "./apiClient";

// Admin console session (POST /api/v1/admin/auth/login). The server is authoritative: every
// /admin/* and /schedule API call is verified there. The client only keeps the issued session
// for this browser tab and drops it on logout, expiry, or any 401.
//
// The token lives in sessionStorage (tab-scoped, cleared when the tab closes) and is sent only
// in the Authorization header — never placed in the page URL. Passwords are never stored.

export interface AdminSession {
  token: string;
  /** ISO-8601 expiry issued by the server. */
  expiresAt: string;
  displayName: string;
  roles: string[];
  /** True while the documented bootstrap password is still in use. */
  mustChangePassword: boolean;
}

const STORAGE_KEY = "vc.adminSession";

/** Treat a session as expired slightly early so a request never races the server-side expiry. */
export const EXPIRY_SKEW_MS = 5_000;

export function isSessionLive(session: AdminSession | null, nowMs: number): session is AdminSession {
  if (!session?.token) return false;
  const exp = Date.parse(session.expiresAt);
  return Number.isFinite(exp) && exp - EXPIRY_SKEW_MS > nowMs;
}

export function readAdminSession(nowMs: number = Date.now()): AdminSession | null {
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as AdminSession;
    if (isSessionLive(parsed, nowMs)) return parsed;
  } catch {
    // corrupt entry — fall through and clear it
  }
  clearAdminSession();
  return null;
}

export function storeAdminSession(session: AdminSession): AdminSession {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage can be unavailable in hardened browsers; the session stays in component state.
  }
  return session;
}

export function clearAdminSession(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * True when an API failure means the admin session is no longer valid server-side. The
 * server's admin gate answers such requests 401 with problem type VC-AUTH-001; other 401s
 * (wrong sign-in password, wrong recording-view credentials) are form errors, not expiry.
 */
export function isSessionRejected(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401 && error.problem.type === "VC-AUTH-001";
}

export async function signIn(username: string, password: string): Promise<AdminSession> {
  return storeAdminSession(await api.post<AdminSession>("/admin/auth/login", { username, password }, ""));
}

export async function changePassword(
  session: AdminSession,
  currentPassword: string,
  newPassword: string,
  confirmPassword: string
): Promise<AdminSession> {
  const next = await api.post<AdminSession>(
    "/admin/auth/password", { currentPassword, newPassword, confirmPassword }, session.token
  );
  return storeAdminSession(next);
}

export async function signOut(session: AdminSession | null): Promise<void> {
  clearAdminSession();
  if (!session) return;
  // Best-effort server-side revocation; the local session is already gone either way.
  await api.post<void>("/admin/auth/logout", undefined, session.token).catch(() => undefined);
}

export interface AdminSessionStatus {
  displayName: string;
  roles: string[];
  expiresAt: string;
  mustChangePassword: boolean;
}

export function fetchSessionStatus(session: AdminSession): Promise<AdminSessionStatus> {
  return api.get<AdminSessionStatus>("/admin/auth/session", session.token);
}
